import type { CompanionActionDto, CompanionFeedDto } from '@fluenceur/types';

/**
 * What the companion has to say right now, read from the feed and nothing else.
 *
 * One resolver for the trigger and for the panel, so the pill and the panel it
 * opens can never disagree — a trigger saying "3 opportunities" over an empty
 * panel is the kind of contradiction that costs the whole surface its credit.
 */
export type CompanionFeedState =
  /** Cards are waiting to be read. */
  | 'opportunities'
  /** No run has ever succeeded: the first briefing is still being prepared. */
  | 'firstRun'
  /** The last attempt failed, or the last success is too old to trust. */
  | 'needsCheck'
  /** The night published cards and none is left on screen. */
  | 'allHandled'
  /** The night ran and found nothing worth the brand's time. */
  | 'zero';

/** What the trigger can show. The two quiet states read the same in the pill. */
export type CompanionTriggerState = 'opportunities' | 'firstRun' | 'needsCheck' | 'idle';

/** Only the cards the brand is meant to see. Waiting cards hold a slot, not a place. */
export const visibleCompanionActions = (feed: CompanionFeedDto): CompanionActionDto[] =>
  feed.actions.filter((action) => action.visibility === 'visible');

/**
 * How many cards the pill announces. Counted from the deck the panel will show
 * rather than carried alongside it, so the number on the pill and the cards
 * behind it cannot disagree after an optimistic answer.
 */
export const visibleCompanionActionCount = (feed: CompanionFeedDto): number =>
  visibleCompanionActions(feed).length;

export const resolveCompanionFeedState = (feed: CompanionFeedDto): CompanionFeedState => {
  if (visibleCompanionActions(feed).length > 0) return 'opportunities';
  // Order matters: a brand that never had a successful run is waiting for its
  // first briefing, which is not the same news as a briefing that went stale.
  if (!feed.lastSuccessfulRun) return 'firstRun';
  if (feed.lastAttempt?.status === 'failed' || feed.stale) return 'needsCheck';
  // The run published cards and none of them is on screen any more: they were
  // answered. Nothing published is a genuinely quiet night.
  return feed.lastSuccessfulRun.counters.published > 0 ? 'allHandled' : 'zero';
};

export const resolveCompanionTriggerState = (feed: CompanionFeedDto): CompanionTriggerState => {
  const state = resolveCompanionFeedState(feed);
  return state === 'allHandled' || state === 'zero' ? 'idle' : state;
};
